import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, BookOpen, Target, Award, FileText, TrendingUp, Lock, Unlock, Building, Users, Briefcase, BarChart3, Scale, Landmark } from 'lucide-react';
import SEO from '../components/SEO';

const ISEElementQuestionBankSalesPage = () => {
  const [accessCode, setAccessCode] = useState('');
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleVerifyAccess = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/books/ise-element-bank/check-access?token=${encodeURIComponent(accessCode)}&email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.valid) {
        setIsVerified(true);
      } else {
        setError('Invalid access code or email. Please check your credentials and try again.');
      }
    } catch (err) {
      setError('Unable to verify access. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const elements = [
    {
      id: 1,
      title: "Element 1",
      subtitle: "Managing Institutional Client Relationships",
      questions: 100,
      topics: ["Marketplace Structure", "Types of Traders", "KYC Requirements", "Documentation"],
      accent: "#1e40af",
      accentLight: "#dbeafe",
      icon: Users
    },
    {
      id: 2,
      title: "Element 2",
      subtitle: "Conflicts of Interest & Standards of Conduct",
      questions: 100,
      topics: ["Information Barriers", "Conflicts Management", "Code of Conduct", "Regulatory Framework"],
      accent: "#7c3aed",
      accentLight: "#ede9fe",
      icon: Scale
    },
    {
      id: 3,
      title: "Element 3",
      subtitle: "Fixed Income",
      questions: 100,
      topics: ["Bond Pricing", "Yield Calculations", "Credit Risk", "Fixed Income Markets"],
      accent: "#dc2626",
      accentLight: "#fee2e2",
      icon: TrendingUp
    },
    {
      id: 4,
      title: "Element 4",
      subtitle: "Equities",
      questions: 100,
      topics: ["Equity Valuation", "Market Mechanics", "Trading Strategies", "Corporate Actions"],
      accent: "#0d9488",
      accentLight: "#ccfbf1",
      icon: BarChart3
    },
    {
      id: 5,
      title: "Element 5",
      subtitle: "Securities Analysis & Investment Theory",
      questions: 100,
      topics: ["Fundamental Analysis", "Technical Analysis", "Portfolio Theory", "Risk Management"],
      accent: "#16a34a",
      accentLight: "#dcfce7",
      icon: Target
    },
    {
      id: 6,
      title: "Element 6",
      subtitle: "Managed & Other Products",
      questions: 100,
      topics: ["Mutual Funds", "ETFs", "Alternative Investments", "Structured Products"],
      accent: "#ea580c",
      accentLight: "#ffedd5",
      icon: Briefcase
    },
    {
      id: 7,
      title: "Element 7",
      subtitle: "Institutional Trading & Operations",
      questions: 100,
      topics: ["Order Execution", "Settlement", "Clearing", "Compliance"],
      accent: "#be185d",
      accentLight: "#fce7f3",
      icon: Landmark
    }
  ];

  const features = [
    { icon: FileText, title: "700 Practice Questions", description: "100 questions per element covering all ISE topics" },
    { icon: Target, title: "Element-Focused", description: "Master each ISE element independently" },
    { icon: TrendingUp, title: "Progressive Learning", description: "Build knowledge systematically" },
    { icon: CheckCircle, title: "Instant Feedback", description: "Detailed explanations for every question" },
    { icon: Award, title: "Exam-Ready", description: "Questions mirror actual ISE difficulty" },
    { icon: Shield, title: "CIRO Aligned", description: "Content matches official ISE syllabus" }
  ];

  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "ISE Element-Wise Question Bank - Investment Services Exam Practice",
    "description": "Master the CIRO Investment Services Exam with 700 practice questions organized by element. 100 questions per element with detailed explanations.",
    "provider": {
      "@type": "Organization",
      "name": "Vitta Finance Academy",
      "sameAs": "https://vittafinance.com"
    },
    "offers": {
      "@type": "Offer",
      "price": "49.99",
      "priceCurrency": "CAD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEO 
        title="ISE Element Question Bank | 700 Practice Questions | 7 Elements | Vitta Finance"
        description="Master the CIRO Investment Services Exam with 700 practice questions organized by element. 100 questions per element covering all ISE topics with detailed explanations."
        keywords="ISE question bank, ISE practice questions, Investment Services Exam questions, CIRO ISE prep, ISE element practice, ISE study materials"
      />
      
      <script type="application/ld+json">
        {JSON.stringify(jsonLdSchema)}
      </script>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)',
        padding: '80px 20px',
        color: 'white'
      }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(212, 175, 55, 0.2)',
            border: '1px solid #d4af37',
            borderRadius: '20px',
            padding: '6px 16px',
            fontSize: '13px',
            fontWeight: '600',
            color: '#d4af37',
            marginBottom: '20px',
            letterSpacing: '0.5px'
          }}>
            INVESTMENT SERVICES EXAM
          </div>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '700',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            ISE Element-Wise Question Bank
          </h1>
          <p style={{
            fontSize: '1.2rem',
            opacity: '0.9',
            maxWidth: '700px',
            margin: '0 auto 30px',
            lineHeight: '1.6'
          }}>
            Master each ISE element with 700 targeted practice questions. 
            100 questions per element with detailed explanations.
          </p>
          <div style={{
            display: 'flex',
            gap: '30px',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginTop: '30px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#d4af37' }}>700</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Practice Questions</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#d4af37' }}>7</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>ISE Elements</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#d4af37' }}>100</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Questions/Element</div>
            </div>
          </div>
        </div>
      </section>

      {/* Access Section */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          {!isVerified ? (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <Lock size={28} color="white" />
                </div>
                <h2 style={{ fontSize: '1.5rem', color: '#0f172a', marginBottom: '10px' }}>
                  Access Your Question Bank
                </h2>
                <p style={{ color: '#64748b', fontSize: '0.95rem' }}>
                  Enter your access code and email to unlock all 7 ISE element question banks
                </p>
              </div>

              <form onSubmit={handleVerifyAccess}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="ise-element-email-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      fontSize: '1rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#1e40af'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '8px'
                  }}>
                    Access Code
                  </label>
                  <input
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    placeholder="Enter your access code"
                    required
                    data-testid="ise-element-access-code-input"
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      fontSize: '1rem',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#1e40af'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                {error && (
                  <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    marginBottom: '20px'
                  }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="ise-element-verify-btn"
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: 'white',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    cursor: isLoading ? 'wait' : 'pointer',
                    opacity: isLoading ? 0.7 : 1,
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {isLoading ? 'Verifying...' : 'Unlock Question Banks'}
                </button>
              </form>

              <div style={{
                marginTop: '30px',
                paddingTop: '30px',
                borderTop: '1px solid #e2e8f0',
                textAlign: 'center'
              }}>
                <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '15px' }}>
                  Need access? Purchase the ISE Element Question Bank
                </p>
                <a
                  href="https://vittafinance.mykajabi.com/offers/VtST24DM"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ise-element-purchase-btn"
                  style={{
                    display: 'inline-block',
                    padding: '12px 24px',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    color: '#1e3a8a',
                    background: '#d4af37',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    transition: 'transform 0.2s'
                  }}
                >
                  Purchase for CAD $49.99
                </a>
              </div>
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              border: '2px solid #16a34a',
              textAlign: 'center'
            }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Unlock size={28} color="white" />
              </div>
              <h2 style={{ fontSize: '1.5rem', color: '#0f172a', marginBottom: '10px' }}>
                Access Granted
              </h2>
              <p style={{ color: '#64748b', fontSize: '0.95rem' }}>
                Your question banks are now unlocked. Scroll down to start practicing.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Elements Grid */}
      <section style={{ padding: '60px 20px', background: 'white' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: '1.75rem',
            color: '#0f172a',
            marginBottom: '40px'
          }}>
            7 ISE Elements - 700 Questions
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {elements.map((element) => {
              const IconComponent = element.icon;
              return (
                <div
                  key={element.id}
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    border: `2px solid ${isVerified ? element.accent : '#e2e8f0'}`,
                    padding: '28px',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: isVerified ? 'pointer' : 'default',
                    opacity: isVerified ? 1 : 0.7
                  }}
                  onMouseOver={(e) => {
                    if (isVerified) {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.1)';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: element.accentLight,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <IconComponent size={24} color={element.accent} />
                    </div>
                    <div>
                      <h3 style={{
                        fontSize: '1.1rem',
                        fontWeight: '700',
                        color: '#0f172a',
                        margin: 0
                      }}>
                        {element.title}
                      </h3>
                      <span style={{
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        color: element.accent
                      }}>
                        {element.questions} Questions
                      </span>
                    </div>
                  </div>

                  <p style={{
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '12px'
                  }}>
                    {element.subtitle}
                  </p>

                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    marginBottom: '16px'
                  }}>
                    {element.topics.map((topic, idx) => (
                      <span
                        key={idx}
                        style={{
                          fontSize: '0.75rem',
                          padding: '4px 10px',
                          background: element.accentLight,
                          color: element.accent,
                          borderRadius: '12px',
                          fontWeight: '500'
                        }}
                      >
                        {topic}
                      </span>
                    ))}
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingTop: '16px',
                    borderTop: '1px solid #f1f5f9'
                  }}>
                    {isVerified ? (
                      <Link
                        to={`/books/ise-element-practice/element-${element.id}`}
                        data-testid={`ise-element-${element.id}-link`}
                        style={{
                          padding: '8px 16px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          color: 'white',
                          background: element.accent,
                          borderRadius: '6px',
                          textDecoration: 'none',
                          transition: 'opacity 0.2s'
                        }}
                      >
                        Start Practice
                      </Link>
                    ) : (
                      <Lock size={18} color="#94a3b8" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: '1.75rem',
            color: '#0f172a',
            marginBottom: '40px'
          }}>
            What's Included
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'flex-start'
                  }}
                >
                  <div style={{
                    width: '44px',
                    height: '44px',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <IconComponent size={20} color="white" />
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#0f172a',
                      marginBottom: '6px'
                    }}>
                      {feature.title}
                    </h3>
                    <p style={{
                      fontSize: '0.9rem',
                      color: '#64748b',
                      margin: 0,
                      lineHeight: '1.5'
                    }}>
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '60px 20px',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.75rem', marginBottom: '15px' }}>
            Ready to Master Each ISE Element?
          </h2>
          <p style={{ opacity: '0.9', marginBottom: '25px', fontSize: '1.05rem' }}>
            Get instant access to 700 practice questions across all 7 ISE elements
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://vittafinance.mykajabi.com/offers/VtST24DM"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '14px 28px',
                fontSize: '1rem',
                fontWeight: '600',
                color: '#1e3a8a',
                background: '#d4af37',
                borderRadius: '8px',
                textDecoration: 'none'
              }}
            >
              Get Question Bank - CAD $49.99
            </a>
            <Link
              to="/ise-study-guide"
              style={{
                padding: '14px 28px',
                fontSize: '1rem',
                fontWeight: '600',
                color: 'white',
                background: 'transparent',
                border: '2px solid white',
                borderRadius: '8px',
                textDecoration: 'none'
              }}
            >
              View ISE Study Guide
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ISEElementQuestionBankSalesPage;
